import { z } from 'zod';

import { ID_PREFIXES, TerminalSessionId, ULID_PATTERN } from '../common/ids';

/**
 * Codec de los QR de Fermin (research R-6, R-10; contracts/terminal-api.md "Bootstrap Code").
 *
 * Una sola implementación compartida por Marker (emite), Staff (lee, feature 004) y API (verifica,
 * feature 004). **Sin dependencias de Node**: funciones puras sobre `string` y `Uint8Array`.
 *
 * ```text
 * FM1B:<ULID26>:<BT20>            Bootstrap Code (52 caracteres, QR v3-M)
 * FM1A:<ULID26>:<slot>:<TOKEN16>  frame autónomo (≈ 59 caracteres, QR v4-M)
 * FM1C:<c8>:<i>:<t16>             frame de challenge VPP (32–33 caracteres, QR v2-M; 004)
 * ```
 *
 * `FM` = Fermin, `1` = versión, `B`/`A`/`C` = tipo. `<ULID26>` es el `tsid` (`tss_<ULID>`) **sin**
 * prefijo, porque `_` no pertenece al modo alfanumérico de QR. Los tokens van en Crockford base32
 * mayúsculas (`bt = crockford32(trunc96(HMAC-SHA256(secret, `${tsid}|${slot}`)))`, 12 bytes → 20
 * caracteres; `token = crockford32(trunc80(HMAC-SHA256(terminal_key, `${tsid}|${slot}`)))`, 10
 * bytes → 16 caracteres). El HMAC lo calcula cada lado (API con `node:crypto`, Marker con
 * `crypto.subtle`); aquí solo viven la cadena a firmar y la codificación.
 */

export const FERMIN_QR_VERSION = 1;

export const BOOTSTRAP_QR_PREFIX = 'FM1B';
export const AUTONOMOUS_QR_PREFIX = 'FM1A';
export const CHALLENGE_QR_PREFIX = 'FM1C';

/** Alfabeto Crockford base32 (el de ULID): sin I, L, O, U. */
export const CROCKFORD32_ALPHABET = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';

/** 96 bits → 20 caracteres crockford32. */
export const BOOTSTRAP_TOKEN_BYTES = 12;
export const BOOTSTRAP_TOKEN_LENGTH = 20;
/** 80 bits → 16 caracteres crockford32. */
export const AUTONOMOUS_TOKEN_BYTES = 10;
export const AUTONOMOUS_TOKEN_LENGTH = 16;
/**
 * Identificador corto del challenge (`c` del `FM1C`, feature 004): 40 bits → 8 caracteres.
 * `c = crockford32Encode(sha256(challenge_id).subarray(0, CHALLENGE_SHORT_ID_BYTES))`.
 * **No** es el `challenge_id` ni su ULID: solo desambigua entre challenges vivos para que Staff
 * agrupe los frames de uno y descarte los de otro marcador en el campo de visión.
 */
export const CHALLENGE_SHORT_ID_BYTES = 5;
export const CHALLENGE_SHORT_ID_LENGTH = 8;
/**
 * Token de frame de challenge: 80 bits → 16 caracteres (mismo tamaño que el token autónomo).
 * `t = crockford32Encode(trunc80(HMAC-SHA256(VPP_FRAME_SECRET, challengeSigningString(...))))`.
 */
export const CHALLENGE_TOKEN_BYTES = 10;
export const CHALLENGE_TOKEN_LENGTH = 16;
/** `FM1B:` (5) + ULID (26) + `:` (1) + BT (20). */
export const BOOTSTRAP_QR_LENGTH = 52;

const CROCKFORD_CHAR = '[0-9A-HJKMNP-TV-Z]';
/** Entero decimal sin ceros a la izquierda (cabe en `Number.MAX_SAFE_INTEGER`). */
const DECIMAL = '(?:0|[1-9][0-9]{0,15})';

export const BOOTSTRAP_TOKEN_PATTERN = new RegExp(`^${CROCKFORD_CHAR}{${BOOTSTRAP_TOKEN_LENGTH}}$`);
export const AUTONOMOUS_TOKEN_PATTERN = new RegExp(
  `^${CROCKFORD_CHAR}{${AUTONOMOUS_TOKEN_LENGTH}}$`,
);
export const CHALLENGE_SHORT_ID_PATTERN = new RegExp(
  `^${CROCKFORD_CHAR}{${CHALLENGE_SHORT_ID_LENGTH}}$`,
);
export const CHALLENGE_TOKEN_PATTERN = new RegExp(
  `^${CROCKFORD_CHAR}{${CHALLENGE_TOKEN_LENGTH}}$`,
);

export const BOOTSTRAP_QR_PATTERN = new RegExp(
  `^${BOOTSTRAP_QR_PREFIX}:(${ULID_PATTERN}):(${CROCKFORD_CHAR}{${BOOTSTRAP_TOKEN_LENGTH}})$`,
);
export const AUTONOMOUS_QR_PATTERN = new RegExp(
  `^${AUTONOMOUS_QR_PREFIX}:(${ULID_PATTERN}):(${DECIMAL}):(${CROCKFORD_CHAR}{${AUTONOMOUS_TOKEN_LENGTH}})$`,
);
/** Forma reservada (004): `c8` y `t16` en alfanumérico QR mayúsculas; `i` decimal. */
export const CHALLENGE_QR_PATTERN = new RegExp(
  `^${CHALLENGE_QR_PREFIX}:([0-9A-Z]{8}):(${DECIMAL}):([0-9A-Z]{16})$`,
);

export const BootstrapTokenSchema = z.string().regex(BOOTSTRAP_TOKEN_PATTERN, {
  message: `Token bootstrap inválido: ${BOOTSTRAP_TOKEN_LENGTH} caracteres crockford32`,
});
export type BootstrapToken = z.infer<typeof BootstrapTokenSchema>;

export const AutonomousTokenSchema = z.string().regex(AUTONOMOUS_TOKEN_PATTERN, {
  message: `Token autónomo inválido: ${AUTONOMOUS_TOKEN_LENGTH} caracteres crockford32`,
});
export type AutonomousToken = z.infer<typeof AutonomousTokenSchema>;

export const ChallengeShortIdSchema = z.string().regex(CHALLENGE_SHORT_ID_PATTERN, {
  message: `Identificador corto de challenge inválido: ${CHALLENGE_SHORT_ID_LENGTH} caracteres crockford32`,
});
export type ChallengeShortId = z.infer<typeof ChallengeShortIdSchema>;

export const ChallengeTokenSchema = z.string().regex(CHALLENGE_TOKEN_PATTERN, {
  message: `Token de frame inválido: ${CHALLENGE_TOKEN_LENGTH} caracteres crockford32`,
});
export type ChallengeToken = z.infer<typeof ChallengeTokenSchema>;

// ---------------------------------------------------------------------------
// Payloads lógicos
// ---------------------------------------------------------------------------

/** `{ v: 1, tsid: 'tss_<ULID>', bt: string(20) }` (research R-6). */
export const BootstrapPayloadSchema = z.object({
  v: z.literal(FERMIN_QR_VERSION),
  tsid: TerminalSessionId,
  bt: BootstrapTokenSchema,
});
export type BootstrapPayload = z.infer<typeof BootstrapPayloadSchema>;

/** `{ v: 1, tsid, slot, token: string(16) }` (research R-10). `slot = floor(synced_ms / AUTONOMOUS_SLOT_MS)`. */
export const AutonomousPayloadSchema = z.object({
  v: z.literal(FERMIN_QR_VERSION),
  tsid: TerminalSessionId,
  slot: z.number().int().nonnegative(),
  token: AutonomousTokenSchema,
});
export type AutonomousPayload = z.infer<typeof AutonomousPayloadSchema>;

/** Reservado para la 004: `{ v: 1, c: <c8>, i: <índice de frame>, t: <t16> }`. */
export const ChallengePayloadSchema = z.object({
  v: z.literal(FERMIN_QR_VERSION),
  c: z.string().regex(/^[0-9A-Z]{8}$/),
  i: z.number().int().nonnegative(),
  t: z.string().regex(/^[0-9A-Z]{16}$/),
});
export type ChallengePayload = z.infer<typeof ChallengePayloadSchema>;

export type DecodedFerminQr =
  | ({ kind: 'bootstrap' } & BootstrapPayload)
  | ({ kind: 'autonomous' } & AutonomousPayload)
  | ({ kind: 'challenge' } & ChallengePayload);

// ---------------------------------------------------------------------------
// Crockford base32
// ---------------------------------------------------------------------------

/**
 * Codifica `bytes` como entero big-endian en Crockford base32, sin relleno: `ceil(8·n / 5)`
 * caracteres, alineado a la derecha (los bits sobrantes se rellenan con ceros por la izquierda).
 * Misma convención que `crockford32From128Bits` de la API para 16 bytes (`dev_…`).
 * 12 bytes → 20 caracteres (`bt`); 10 bytes → 16 caracteres (token autónomo).
 */
export function crockford32Encode(bytes: Uint8Array): string {
  const totalBits = bytes.length * 8;
  const chars = Math.ceil(totalBits / 5);
  let out = '';
  let buffer = 0;
  // Ceros de relleno por la izquierda para que el total de bits sea múltiplo de 5.
  let bitsInBuffer = chars * 5 - totalBits;
  for (const byte of bytes) {
    buffer = (buffer << 8) | byte;
    bitsInBuffer += 8;
    while (bitsInBuffer >= 5) {
      bitsInBuffer -= 5;
      out += CROCKFORD32_ALPHABET[(buffer >>> bitsInBuffer) & 31];
    }
    buffer &= (1 << bitsInBuffer) - 1;
  }
  return out;
}

// ---------------------------------------------------------------------------
// Cadenas a firmar (el HMAC lo hace cada lado)
// ---------------------------------------------------------------------------

/** `${tsid}|${slot}` con el `tsid` **con** prefijo (`tss_…`); clave `TERMINAL_BOOTSTRAP_SECRET`. */
export function bootstrapSigningString(tsid: string, slot: number): string {
  return `${tsid}|${slot}`;
}

/** `${tsid}|${slot}` con el `tsid` **con** prefijo (`tss_…`); clave = clave de terminal (HMAC-SHA256). */
export function autonomousSigningString(tsid: string, slot: number): string {
  return `${tsid}|${slot}`;
}

/**
 * `${challengeId}|${nonce}|${index}` con el `challengeId` **con** prefijo (`chl_…`) y el `nonce`
 * base64url tal cual se persistió; clave = `VPP_FRAME_SECRET` del servidor (HMAC-SHA256, FR-017).
 * El token del frame es `crockford32Encode(mac.subarray(0, CHALLENGE_TOKEN_BYTES))`.
 *
 * Solo el servidor conoce el secreto y el nonce: ni el marcador ni Staff pueden derivar un frame
 * (docs/vpp-protocol.md §5.2). El HMAC lo calcula cada lado con su propia primitiva; aquí solo
 * vive la cadena a firmar, igual que en bootstrap y autónomo.
 */
export function challengeSigningString(challengeId: string, nonce: string, index: number): string {
  return `${challengeId}|${nonce}|${index}`;
}

// ---------------------------------------------------------------------------
// Encoder / decoder
// ---------------------------------------------------------------------------

function stripTsidPrefix(tsid: string): string {
  return tsid.slice(ID_PREFIXES.terminalSession.length + 1);
}

function withTsidPrefix(ulid: string): string {
  return `${ID_PREFIXES.terminalSession}_${ulid}`;
}

/** `FM1B:<ULID>:<BT>` (52 caracteres). Lanza `ZodError` si el payload no es válido. */
export function encodeBootstrapQr(payload: BootstrapPayload): string {
  const { tsid, bt } = BootstrapPayloadSchema.parse(payload);
  return `${BOOTSTRAP_QR_PREFIX}:${stripTsidPrefix(tsid)}:${bt}`;
}

/** `FM1A:<ULID>:<slot decimal>:<TOKEN>`. Lanza `ZodError` si el payload no es válido. */
export function encodeAutonomousQr(payload: AutonomousPayload): string {
  const { tsid, slot, token } = AutonomousPayloadSchema.parse(payload);
  return `${AUTONOMOUS_QR_PREFIX}:${stripTsidPrefix(tsid)}:${slot}:${token}`;
}

/**
 * Validación estricta para emitir un `FM1C`: el alfabeto real es crockford32, mientras que
 * `ChallengePayloadSchema` (forma reservada de la 003, intacta) admite el superconjunto
 * `[0-9A-Z]`. Todo lo que valide aquí valida también allí.
 */
const ChallengeQrEncodeSchema = z.object({
  v: z.literal(FERMIN_QR_VERSION),
  c: ChallengeShortIdSchema,
  i: z.number().int().nonnegative(),
  t: ChallengeTokenSchema,
});

/**
 * `FM1C:<c8>:<i>:<t16>` (32–33 caracteres, QR v2-M). Lanza `ZodError` si el payload no es válido.
 * El marcador lo llama por cada frame de `challenge_start`; nunca fabrica ni deriva un token.
 */
export function encodeChallengeQr(payload: ChallengePayload): string {
  const { c, i, t } = ChallengeQrEncodeSchema.parse(payload);
  return `${CHALLENGE_QR_PREFIX}:${c}:${i}:${t}`;
}

/**
 * Decodifica el texto de un QR de Fermin. Devuelve `null` para cualquier cadena ajena (otro
 * QR, JSON, un `tss_…` en claro, prefijo desconocido, token de longitud incorrecta…). Nunca lanza.
 */
export function decodeFerminQr(text: string): DecodedFerminQr | null {
  if (typeof text !== 'string') {
    return null;
  }
  const bootstrap = BOOTSTRAP_QR_PATTERN.exec(text);
  if (bootstrap) {
    return {
      kind: 'bootstrap',
      v: FERMIN_QR_VERSION,
      tsid: withTsidPrefix(bootstrap[1]!),
      bt: bootstrap[2]!,
    };
  }
  const autonomous = AUTONOMOUS_QR_PATTERN.exec(text);
  if (autonomous) {
    return {
      kind: 'autonomous',
      v: FERMIN_QR_VERSION,
      tsid: withTsidPrefix(autonomous[1]!),
      slot: Number(autonomous[2]),
      token: autonomous[3]!,
    };
  }
  const challenge = CHALLENGE_QR_PATTERN.exec(text);
  if (challenge) {
    return {
      kind: 'challenge',
      v: FERMIN_QR_VERSION,
      c: challenge[1]!,
      i: Number(challenge[2]),
      t: challenge[3]!,
    };
  }
  return null;
}
